import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import Icon from "@mui/material/Icon";
import InputAdornment from "@mui/material/InputAdornment";
import Popover from "@mui/material/Popover";
import { alpha } from "@mui/material/styles";
import MDInput from "components/MDInput";

const INK = "#1f1f1f";
const pad = (n) => String(n).padStart(2, "0");

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];

/* ------------------------------ helpers ------------------------------ */

const parseDate = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || "");
  return m ? { y: +m[1], m: +m[2] - 1, d: +m[3] } : null;
};
const toISO = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const todayISO = () => {
  const n = new Date();
  return toISO(n.getFullYear(), n.getMonth(), n.getDate());
};
const displayDate = (v) => {
  const p = parseDate(v);
  return p ? `${pad(p.d)}-${pad(p.m + 1)}-${p.y}` : "";
};

const parseTime = (v) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(v || "");
  return m ? { h: +m[1], m: +m[2] } : null;
};
const displayTime = (v) => {
  const t = parseTime(v);
  if (!t) return "";
  return `${pad(t.h % 12 || 12)}:${pad(t.m)} ${t.h >= 12 ? "PM" : "AM"}`;
};

/* ------------------------------ shared bits ------------------------------ */

const textButtonSx = ({ palette }) => ({
  padding: "6px 12px",
  borderRadius: "8px",
  fontSize: { xs: 13, sm: 14 },
  fontWeight: 700,
  color: palette.primary.main,
  "&:hover": { backgroundColor: alpha(palette.primary.main, 0.08) },
  "&.Mui-disabled": { color: "#bdbdbd" },
});

const filledButtonSx = ({ palette }) => ({
  padding: "6px 18px",
  borderRadius: "8px",
  fontSize: { xs: 13, sm: 14 },
  fontWeight: 700,
  color: palette.white.main,
  backgroundColor: palette.primary.main,
  "&:hover": { backgroundColor: palette.primary.focus },
});

const goldButtonSx = ({ palette }) => ({
  padding: "6px 12px",
  borderRadius: "8px",
  fontSize: { xs: 13, sm: 14 },
  fontWeight: 700,
  color: palette.secondary.focus,
  "&:hover": { backgroundColor: alpha(palette.secondary.main, 0.16) },
  "&.Mui-disabled": { color: "#bdbdbd" },
});

const arrowSx = ({ palette }) => ({
  width: { xs: 30, sm: 34 },
  height: { xs: 30, sm: 34 },
  borderRadius: "50%",
  color: palette.primary.main,
  "&:hover": { backgroundColor: alpha(palette.primary.main, 0.08) },
  "&.Mui-disabled": { color: "#c8c8c8" },
});

/* ------------------------------ calendar ------------------------------ */

const DatePanel = ({ value, min, max, onPick, onClear }) => {
  const sel = parseDate(value);
  const today = parseDate(todayISO());

  const minD = parseDate(min) ? min : null;
  const maxD = parseDate(max) ? max : null;
  const isOut = (iso) => (minD && iso < minD) || (maxD && iso > maxD);

  // Open on the selected date's month. If there is no value, or the value is outside the
  // allowed range (min / max = event dates), open on today's month; and if today is outside
  // the range too, open on the event's month instead
  const start = sel && !isOut(value) ? sel : isOut(todayISO()) ? parseDate(minD || maxD) : today;

  const [view, setView] = useState({ y: start.y, m: start.m });
  const [mode, setMode] = useState("days"); // "days" | "years"
  const yearsRef = useRef(null);

  const yearFrom = minD ? parseDate(minD).y : 1900;
  const yearTo = maxD ? parseDate(maxD).y : today.y + 30;
  const years = [];
  for (let y = yearFrom; y <= yearTo; y += 1) years.push(y);

  // when the year list opens, scroll the selected year into view
  useEffect(() => {
    if (mode !== "years") return;
    const c = yearsRef.current;
    const el = c && c.querySelector('[data-selected="true"]');
    if (c && el) c.scrollTop = el.offsetTop - c.clientHeight / 2 + el.clientHeight / 2;
  }, [mode]);

  const go = (delta) =>
    setView(({ y, m }) => {
      const n = new Date(y, m + delta, 1);
      return { y: n.getFullYear(), m: n.getMonth() };
    });

  const firstWeekday = new Date(view.y, view.m, 1).getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [...Array(firstWeekday).fill(null)];
  for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);

  const prevDisabled = minD && toISO(view.y, view.m, 1) <= minD;
  const nextDisabled = maxD && toISO(view.y, view.m, daysInMonth) >= maxD;
  const todayOut = isOut(todayISO());

  return (
    <Box sx={{ width: { xs: 268, sm: 312 }, maxWidth: "100%", p: { xs: 1.5, sm: 2 } }}>
      {/* header: month + year (click to pick a year) and the month arrows */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <ButtonBase
          onClick={() => setMode((m) => (m === "days" ? "years" : "days"))}
          sx={({ palette }) => ({
            display: "flex",
            alignItems: "center",
            gap: "2px",
            padding: { xs: "4px 8px", sm: "6px 10px" },
            borderRadius: "8px",
            fontSize: { xs: 14, sm: 16 },
            fontWeight: 700,
            color: palette.primary.main,
            "&:hover": { backgroundColor: alpha(palette.primary.main, 0.08) },
          })}
        >
          {MONTHS[view.m]} {view.y}
          <Icon fontSize="small">{mode === "years" ? "arrow_drop_up" : "arrow_drop_down"}</Icon>
        </ButtonBase>

        {mode === "days" && (
          <Box sx={{ display: "flex", gap: "2px" }}>
            <ButtonBase sx={arrowSx} disabled={!!prevDisabled} onClick={() => go(-1)}>
              <Icon>chevron_left</Icon>
            </ButtonBase>
            <ButtonBase sx={arrowSx} disabled={!!nextDisabled} onClick={() => go(1)}>
              <Icon>chevron_right</Icon>
            </ButtonBase>
          </Box>
        )}
      </Box>

      {mode === "years" ? (
        <Box
          ref={yearsRef}
          sx={{
            position: "relative",
            height: { xs: 204, sm: 252 },
            overflowY: "auto",
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: "6px",
            alignContent: "start",
            py: 0.5,
          }}
        >
          {years.map((y) => (
            <ButtonBase
              key={y}
              data-selected={y === view.y}
              onClick={() => {
                setView((v) => ({ ...v, y }));
                setMode("days");
              }}
              sx={({ palette }) => ({
                height: { xs: 32, sm: 36 },
                borderRadius: "18px",
                fontSize: { xs: 13, sm: 14 },
                color: y === view.y ? palette.white.main : INK,
                fontWeight: y === view.y ? 700 : 400,
                backgroundColor: y === view.y ? palette.primary.main : "transparent",
                border: y === today.y && y !== view.y ? `1px solid ${palette.secondary.main}` : 0,
                "&:hover": {
                  backgroundColor:
                    y === view.y ? palette.primary.focus : alpha(palette.primary.main, 0.08),
                },
              })}
            >
              {y}
            </ButtonBase>
          ))}
        </Box>
      ) : (
        <>
          {/* weekday letters */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", mb: 0.5 }}>
            {WEEKDAYS.map((w, i) => (
              <Box
                key={`${w}${i}`}
                sx={({ palette }) => ({
                  textAlign: "center",
                  fontSize: { xs: 11, sm: 12 },
                  fontWeight: 700,
                  color: palette.secondary.focus,
                  py: 0.5,
                })}
              >
                {w}
              </Box>
            ))}
          </Box>

          {/* days */}
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", rowGap: "2px" }}>
            {cells.map((d, i) => {
              if (d === null) return <Box key={`e${i}`} />;
              const iso = toISO(view.y, view.m, d);
              const isSel = iso === value;
              const isToday = iso === todayISO();
              const out = isOut(iso);
              return (
                <ButtonBase
                  key={iso}
                  disabled={!!out}
                  onClick={() => onPick(iso)}
                  sx={({ palette }) => ({
                    justifySelf: "center",
                    width: { xs: 32, sm: 36 },
                    height: { xs: 32, sm: 36 },
                    borderRadius: "50%",
                    fontSize: { xs: 13, sm: 14 },
                    fontWeight: isSel ? 700 : 400,
                    color: isSel ? palette.white.main : INK,
                    backgroundColor: isSel ? palette.primary.main : "transparent",
                    border: isToday && !isSel ? `1px solid ${palette.secondary.main}` : 0,
                    "&:hover": {
                      backgroundColor: isSel
                        ? palette.primary.focus
                        : alpha(palette.primary.main, 0.08),
                    },
                    "&.Mui-disabled": { color: "#c4c4c4" },
                  })}
                >
                  {d}
                </ButtonBase>
              );
            })}
          </Box>
        </>
      )}

      {/* footer */}
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1 }}>
        <ButtonBase sx={textButtonSx} onClick={onClear}>
          Clear
        </ButtonBase>
        <ButtonBase sx={goldButtonSx} disabled={todayOut} onClick={() => onPick(todayISO())}>
          Today
        </ButtonBase>
      </Box>
    </Box>
  );
};

/* ------------------------------ time ------------------------------ */

const Column = ({ items, selected, onSelect }) => {
  const ref = useRef(null);

  // scroll the selected item to the middle when the popup opens
  useEffect(() => {
    const c = ref.current;
    const el = c && c.querySelector('[data-selected="true"]');
    if (c && el) c.scrollTop = el.offsetTop - c.clientHeight / 2 + el.clientHeight / 2;
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        position: "relative",
        height: { xs: 180, sm: 228 },
        width: { xs: 56, sm: 64 },
        overflowY: "auto",
        scrollbarWidth: "thin",
        display: "flex",
        flexDirection: "column",
        gap: "4px",
        px: "2px",
      }}
    >
      {items.map((it) => {
        const active = it.value === selected;
        return (
          <ButtonBase
            key={it.value}
            data-selected={active}
            onClick={() => onSelect(it.value)}
            sx={({ palette }) => ({
              flexShrink: 0,
              height: { xs: 32, sm: 36 },
              borderRadius: "8px",
              fontSize: { xs: 13, sm: 15 },
              fontWeight: active ? 700 : 400,
              color: active ? palette.white.main : INK,
              backgroundColor: active ? palette.primary.main : "transparent",
              "&:hover": {
                backgroundColor: active ? palette.primary.focus : alpha(palette.primary.main, 0.08),
              },
            })}
          >
            {it.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
};

const HOURS = Array.from({ length: 12 }, (_, i) => ({ value: i + 1, label: pad(i + 1) }));
const MINUTES = Array.from({ length: 60 }, (_, i) => ({ value: i, label: pad(i) }));
const MERIDIEM = [
  { value: "AM", label: "AM" },
  { value: "PM", label: "PM" },
];

const TimePanel = ({ value, onOk, onClear, onCancel }) => {
  const now = new Date();
  const t = parseTime(value) || { h: now.getHours(), m: now.getMinutes() };
  const [h12, setH12] = useState(t.h % 12 || 12);
  const [mi, setMi] = useState(t.m);
  const [ap, setAp] = useState(t.h >= 12 ? "PM" : "AM");

  const commit = () => onOk(`${pad((h12 % 12) + (ap === "PM" ? 12 : 0))}:${pad(mi)}`);

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2 }, maxWidth: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "center", gap: { xs: "6px", sm: "8px" } }}>
        <Column items={HOURS} selected={h12} onSelect={setH12} />
        <Column items={MINUTES} selected={mi} onSelect={setMi} />
        <Column items={MERIDIEM} selected={ap} onSelect={setAp} />
      </Box>

      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1 }}>
        <ButtonBase sx={textButtonSx} onClick={onClear}>
          Clear
        </ButtonBase>
        <Box sx={{ display: "flex", gap: "6px" }}>
          <ButtonBase sx={goldButtonSx} onClick={onCancel}>
            Cancel
          </ButtonBase>
          <ButtonBase sx={filledButtonSx} onClick={commit}>
            OK
          </ButtonBase>
        </Box>
      </Box>
    </Box>
  );
};

/* ------------------------------ the field ------------------------------ */

const PickerInput = ({
  type,
  name,
  value,
  onChange,
  onBlur,
  inputProps,
  placeholder,
  disabled,
  error,
  fullWidth,
  ...rest
}) => {
  const wrapRef = useRef(null);
  const [anchor, setAnchor] = useState(null);
  const isDate = type === "date";
  const display = isDate ? displayDate(value) : displayTime(value);

  const openPicker = () => {
    if (!disabled) setAnchor(wrapRef.current);
  };

  const close = () => {
    setAnchor(null);
    // after the value was applied, so validation runs on the new value
    if (onBlur) setTimeout(() => onBlur({ target: { name } }), 0);
  };

  const commit = (v) => {
    if (onChange) onChange({ target: { name, value: v } });
    close();
  };

  return (
    <>
      <Box ref={wrapRef} sx={{ width: fullWidth ? "100%" : "auto" }}>
        <MDInput
          {...rest}
          name={name}
          value={display}
          placeholder={placeholder || (isDate ? "dd-mm-yyyy" : "hh:mm AM")}
          onClick={openPicker}
          error={!!error}
          disabled={disabled}
          fullWidth={fullWidth}
          inputProps={{
            readOnly: true,
            style: { cursor: disabled ? "default" : "pointer" },
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
                e.preventDefault();
                openPicker();
              }
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end" sx={{ marginRight: error ? 3 : 0 }}>
                <Icon sx={({ palette }) => ({ color: palette.primary.main, cursor: "pointer" })}>
                  {isDate ? "event" : "schedule"}
                </Icon>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        marginThreshold={8}
        sx={{ zIndex: 20000 }}
        PaperProps={{
          sx: ({ palette }) => ({
            mt: 0.5,
            maxWidth: "calc(100vw - 16px)",
            maxHeight: "calc(100vh - 16px)",
            overflow: "auto",
            borderRadius: "14px",
            border: `1px solid ${alpha(palette.secondary.main, 0.5)}`,
            borderTop: `4px solid ${palette.primary.main}`,
            boxShadow: "0 12px 32px rgba(60, 10, 10, 0.18)",
            backgroundColor: "#ffffff",
            backgroundImage: "none",
            opacity: 1,
          }),
        }}
      >
        {isDate ? (
          <DatePanel
            value={value}
            min={inputProps && inputProps.min}
            max={inputProps && inputProps.max}
            onPick={commit}
            onClear={() => commit("")}
          />
        ) : (
          <TimePanel
            value={value}
            onOk={commit}
            onClear={() => commit("")}
            onCancel={() => setAnchor(null)}
          />
        )}
      </Popover>
    </>
  );
};

const DateTimeInput = ({ type, ...props }) => {
  if (type === "date" || type === "time") return <PickerInput type={type} {...props} />;
  return <MDInput type={type} {...props} />;
};

DateTimeInput.propTypes = { type: PropTypes.string };
DateTimeInput.defaultProps = { type: "text" };

DatePanel.propTypes = {
  value: PropTypes.string,
  min: PropTypes.string,
  max: PropTypes.string,
  onPick: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
};
TimePanel.propTypes = {
  value: PropTypes.string,
  onOk: PropTypes.func.isRequired,
  onClear: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
Column.propTypes = {
  items: PropTypes.array.isRequired,
  selected: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSelect: PropTypes.func.isRequired,
};
PickerInput.propTypes = {
  type: PropTypes.oneOf(["date", "time"]).isRequired,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  inputProps: PropTypes.object,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  fullWidth: PropTypes.bool,
};

export default DateTimeInput;
